import { differenceInDays, isPast } from "date-fns";
import { observer } from "mobx-react";
import type React from "react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";
import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";

import { ModalRenderType } from "components/Modal/Modal.model";

import { TeacherModals } from "constants/modals-constants";

import { Divider } from "components/Divider/Divider";
import { LoadingSpinner } from "components/LoadingSpinner";

import { Heading2, Heading4 } from "styles/elements/Headings";
import { CssFlex } from "styles/helpers/layout";

import { AddStudentToEmailInviteBundleForm } from "../AddStudentToEmailInviteBundleForm/AddStudentToEmailInviteBundleForm";
import type { IndividualInvite } from "../IndividualInviteList/IndividualInviteList";
import { IndividualInviteList } from "../IndividualInviteList/IndividualInviteList";
import { InviteBundleStatusTag } from "../InviteBundleStatusTag/InviteBundleStatusTag";
import { StyledDetailsBox, StyledModal } from "./EmailInviteBundleDetailsModal.styles";

interface EmailInviteBundleDetailsModalParams {
  emailInviteBundleId: string;
}

export const EmailInviteBundleDetailsModal: React.FC = observer(() => {
  const { t } = useTranslation("teacher-dashboard");
  const { isModalOpen, closeModal, modalParams } = useModal();
  const { emailInviteBundleDetails, fetchEmailInviteBundleDetails, setEmailInviteBundleDetails } =
    useInviteManagement();
  const { loading: isLoading, deleteInviteById, fetchInvitationBundleList } = useProduct();

  const { formatDate } = useLocaleDateFormatter();

  const isCurrentlyOpen = isModalOpen(TeacherModals.ViewEmailInviteBundleDetails);
  const { emailInviteBundleId } = modalParams as unknown as EmailInviteBundleDetailsModalParams;

  const handleDeleteInvite = useCallback(
    (inviteId: string) =>
      deleteInviteById(inviteId).then(() => {
        fetchInvitationBundleList();
        if (emailInviteBundleId) {
          fetchEmailInviteBundleDetails(emailInviteBundleId);
        }
      }),
    [
      deleteInviteById,
      emailInviteBundleId,
      fetchEmailInviteBundleDetails,
      fetchInvitationBundleList,
    ],
  );

  useEffect(() => {
    if (isCurrentlyOpen && emailInviteBundleId) {
      fetchEmailInviteBundleDetails(emailInviteBundleId);
    }

    if (!isCurrentlyOpen) {
      setEmailInviteBundleDetails(null);
    }
  }, [
    isCurrentlyOpen,
    emailInviteBundleId,
    fetchEmailInviteBundleDetails,
    setEmailInviteBundleDetails,
  ]);

  if (!emailInviteBundleId) {
    return null;
  }

  return (
    <StyledModal
      closeOnClickOutside
      dataCy={TeacherModals.ViewEmailInviteBundleDetails}
      isOpen={isCurrentlyOpen}
      renderToElementType={ModalRenderType.REACT_PORTAL}
      onClose={closeModal}
    >
      <CssFlex flexDirection="column" gap={1.5} padding="2rem">
        <Heading2>
          {t("teacher-dashboard:invitationManagement.inviteBundleDetailsModal.title")}
        </Heading2>
        <Divider />
        {emailInviteBundleDetails?.id === emailInviteBundleId ? (
          <>
            {/* -- Bundle information -- */}
            <StyledDetailsBox flexDirection="column" gap={1}>
              <Heading4>
                {t(
                  "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.title",
                )}
              </Heading4>
              <ul>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.properties.status",
                  )}
                  <InviteBundleStatusTag
                    dataCy="email-invite-status"
                    currentUsage={emailInviteBundleDetails.totalAcceptedInvites}
                    maxUsage={emailInviteBundleDetails.totalSentInvites}
                    expirationDate={emailInviteBundleDetails.licenseExpirationDate}
                  />
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.properties.usage",
                  )}
                  <span data-cy="email-invite-usage">
                    {emailInviteBundleDetails.totalAcceptedInvites} /{" "}
                    {emailInviteBundleDetails.totalSentInvites}
                  </span>
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.properties.products",
                  )}
                  <span data-cy="email-invite-products">
                    {emailInviteBundleDetails.productNames}
                  </span>
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.properties.group",
                  )}
                  <span data-cy="email-invite-group">
                    {emailInviteBundleDetails.groupName || "-"}
                  </span>
                </li>
                <li>
                  {t(
                    "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.bundleDetails.properties.expirationDate",
                  )}
                  <span data-cy="email-invite-expiration-date">
                    {formatDate(emailInviteBundleDetails.licenseExpirationDate)}
                  </span>
                </li>
              </ul>
            </StyledDetailsBox>
            {/* -- Form to add more students to list (only if license is not expired) -- */}
            {!isPast(new Date(emailInviteBundleDetails.licenseExpirationDate)) && (
              <AddStudentToEmailInviteBundleForm emailInviteBundleId={emailInviteBundleId} />
            )}
            {/* -- List of individual invites in the bundle -- */}
            <IndividualInviteList
              inviteList={emailInviteBundleDetails.inviteList.map(
                (invite): IndividualInvite => ({
                  id: invite.id,
                  email: invite.email,
                  dateField: invite.acceptedOn,
                  inTargetGroup: invite.inTargetGroup,
                  isRemovable:
                    !invite.accepted &&
                    differenceInDays(new Date(), new Date(invite.createdOn)) <= 14,
                }),
              )}
              // Only show group column for invites that have a group assigned, otherwise it adds no value
              showGroupColumn={!!emailInviteBundleDetails.groupId}
              readOnly={false}
              onDeleteInvite={handleDeleteInvite}
              isLoading={isLoading}
            />
          </>
        ) : (
          <LoadingSpinner />
        )}
      </CssFlex>
    </StyledModal>
  );
});
